'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth, useOrganizationList } from '@clerk/nextjs';
import { useQueryClient } from '@tanstack/react-query';
import { FirstRunSetup } from '@/features/account/components/first-run-setup';
import { useLiveSessionGate } from '@/features/account/lib/live-session';
import { fetchWorkspaces, recoverWorkspace } from '@/features/account/api';
import type { WorkspaceItem } from '@/features/account/types';
import { ApiError } from '@/lib/api-client';
import { useApi } from '@/lib/use-api';

/**
 * Root route.
 *
 * - Signed out → /signup (the funnel entry; it links to sign-in too).
 * - Signed in with an active org → /chat. The membership list is not required.
 * - Signed in, no active org, and the membership list has rows → activate
 *   one (personal first), then /chat.
 * - Signed in, no active org, and a finished list is empty → confirm with
 *   the BFF first: an existing BFF workspace repairs into the app; only a
 *   confirmed-empty BFF renders first-run setup.
 *
 * The signed-out redirect needs only useAuth: never wait on the org list to
 * send visitors to signup. A stuck loader (e.g. a dead session after purge)
 * surfaces a recovery card instead of hanging on "Opening Home…" forever.
 */
const LOAD_TIMEOUT_MS = 10_000;

function LoadingShell() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--paper)] p-6">
      <p className="text-sm text-[var(--body)]">Opening Home…</p>
    </div>
  );
}

function RecoveryShell() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--band)] p-6">
      <div className="w-full max-w-md rounded-xl border border-[var(--line)] bg-white p-6 text-center shadow-sm">
        <div className="mb-4 flex justify-center">
          <Image
            src="/brand/propensify-lockup.svg"
            alt="Propensify"
            width={140}
            height={35}
            priority
            className="h-[30px] w-auto"
          />
        </div>
        <h1 className="text-base font-semibold text-[var(--ink)]">Home is taking a while to open</h1>
        <p className="mt-2 text-sm text-[var(--body)]">
          Check your connection — or head straight to signup and we&apos;ll take it from there.
        </p>
        <div className="mt-4 flex flex-col gap-2">
          <Link
            href="/signup"
            className="inline-flex items-center justify-center rounded-md bg-[var(--lime)] px-4 py-2 text-sm font-semibold text-[var(--ink)] hover:bg-[var(--y-deep)]"
          >
            Go to sign up
          </Link>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center rounded-md border border-[var(--line)] bg-white px-4 py-2 text-sm font-medium text-[var(--ink)] hover:bg-[var(--band)]"
          >
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

function RecoverFailedShell({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--band)] p-6">
      <div className="w-full max-w-md rounded-xl border border-[var(--line)] bg-white p-6 text-center shadow-sm">
        <div className="mb-4 flex justify-center">
          <Image
            src="/brand/propensify-lockup.svg"
            alt="Propensify"
            width={140}
            height={35}
            priority
            className="h-[30px] w-auto"
          />
        </div>
        <h1 className="text-base font-semibold text-[var(--ink)]">
          We found your workspace but couldn&rsquo;t open it
        </h1>
        <p className="mt-2 text-sm text-[var(--body)]">
          Please try again — if this keeps happening, contact support rather than creating a
          second workspace.
        </p>
        <div className="mt-4 flex flex-col gap-2">
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center justify-center rounded-md bg-[var(--lime)] px-4 py-2 text-sm font-semibold text-[var(--ink)] hover:bg-[var(--y-deep)]"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

type BffGate = 'idle' | 'checking' | 'empty' | 'recovering' | 'failed';

/** Minimal membership shape the gate needs (activation + BFF cross-check). */
interface GateMembershipLike {
  organization: {
    id: string;
    name: string;
    publicMetadata?: unknown;
  };
}

function isPersonalOrg(m: GateMembershipLike): boolean {
  return (m.organization.publicMetadata as { kind?: string } | null)?.kind === 'personal';
}

/** Personal-first, then A–Z — the one activation order used by every path. */
function pickPersonalFirst<T extends GateMembershipLike>(list: readonly T[]): T {
  return [...list].sort((a, b) => {
    const aPersonal = isPersonalOrg(a);
    const bPersonal = isPersonalOrg(b);
    if (aPersonal !== bPersonal) return aPersonal ? -1 : 1;
    return a.organization.name.localeCompare(b.organization.name);
  })[0];
}

/**
 * Best-effort extraction of a membership list from a revalidate() result.
 * Clerk types revalidate() as Promise<void> but at runtime it resolves via
 * SWR mutate, which may carry the fresh data. Anything unrecognized (or a
 * void resolve) yields undefined so callers fall back to rendered state.
 */
function asMembershipList(value: unknown): GateMembershipLike[] | undefined {
  if (Array.isArray(value)) return value as GateMembershipLike[];
  if (value && typeof value === 'object' && Array.isArray((value as { data?: unknown }).data)) {
    return (value as { data: GateMembershipLike[] }).data;
  }
  return undefined;
}

export default function Home() {
  const { isLoaded, isSignedIn, orgId } = useAuth();
  // Opt in. With no options Clerk never fetches this list, so data stays []
  // and a signed-in user with an active org sits on "Opening Home…".
  // pageSize covers a user with many orgs: activation only sees this page,
  // and personal-first has to be able to find the personal workspace on it.
  const { userMemberships, setActive, isLoaded: orgListLoaded } = useOrganizationList({
    userMemberships: { pageSize: 100 },
  });
  const router = useRouter();
  const api = useApi();
  const queryClient = useQueryClient();
  const activatingRef = useRef(false);
  const [timedOut, setTimedOut] = useState(false);
  const [bffGate, setBffGate] = useState<BffGate>('idle');
  const [bffRetry, setBffRetry] = useState(0);
  const bffCheckRef = useRef(false);

  const memberships = userMemberships?.data;
  const membershipCount = memberships?.length ?? 0;
  // data is [] both while the fetch is in flight and when the user truly
  // has no orgs. Wait until the fetch finishes before trusting a zero.
  const membershipsKnown =
    orgListLoaded && !userMemberships?.isLoading && memberships !== undefined;
  // R2-15: the one live-identity guard before first-run setup. A purged
  // identity's stale tab ends in sign-out + sign-in, never "Create".
  const sessionGate = useLiveSessionGate(
    Boolean(isLoaded && isSignedIn && !orgId && membershipsKnown && membershipCount === 0 && bffGate === 'empty')
  );

  // Latest-value mirrors so long-running gate flows can re-check freshness
  // without depending on the (identity-unstable) Clerk resource object.
  // Updated in an effect (declared before the gate effects that read them).
  const membershipsRef = useRef<GateMembershipLike[] | undefined>(memberships);
  const revalidateRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const revalidateFn = userMemberships?.revalidate;
  useEffect(() => {
    membershipsRef.current = memberships;
    revalidateRef.current = typeof revalidateFn === 'function' ? revalidateFn : undefined;
  });

  // Refresh Clerk's org list; never throws. Resolves with the revalidated
  // memberships (runtime return when recognizable, else latest rendered).
  const safeRevalidate = useCallback(async (): Promise<GateMembershipLike[] | undefined> => {
    const revalidate = revalidateRef.current;
    if (typeof revalidate !== 'function') return membershipsRef.current;
    try {
      const result = await revalidate();
      return asMembershipList(result) ?? membershipsRef.current;
    } catch {
      return membershipsRef.current;
    }
  }, []);

  const retryBffGate = useCallback(() => {
    bffCheckRef.current = false;
    setBffGate('idle');
    setBffRetry((n) => n + 1);
    // The failure may itself have been stale Clerk state: refresh
    // memberships too, or the re-run gate deterministically re-fails.
    void safeRevalidate();
  }, [safeRevalidate]);

  useEffect(() => {
    // An active org leaves immediately, so this timer only covers Clerk
    // itself and a membership fetch that never settles.
    if (isLoaded && (!isSignedIn || Boolean(orgId) || membershipsKnown)) return;
    const timer = window.setTimeout(() => setTimedOut(true), LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isLoaded, isSignedIn, orgId, membershipsKnown]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      router.replace('/signup');
      return;
    }
    // /chat needs an active org. useAuth already has one, so do not wait
    // for the membership list.
    if (orgId) {
      router.replace('/chat');
    }
  }, [isLoaded, isSignedIn, orgId, router]);

  useEffect(() => {
    // Edge: memberships exist but no org is active yet (fresh tab before
    // Clerk hydrates activation). Activate personal-first, once.
    if (!isLoaded || !membershipsKnown || !isSignedIn || !memberships || membershipCount === 0 || orgId) {
      return;
    }
    if (activatingRef.current) return;
    activatingRef.current = true;
    const first = pickPersonalFirst(memberships);
    setActive?.({ organization: first.organization.id })?.catch(() => {
      activatingRef.current = false;
    });
    // Reset on cleanup: StrictMode remounts (and dep-change reruns) must be
    // able to proceed; a stuck ref would skip activation forever.
    return () => {
      activatingRef.current = false;
    };
  }, [isLoaded, membershipsKnown, isSignedIn, memberships, membershipCount, orgId, setActive]);

  useEffect(() => {
    // A finished empty membership list: ask the BFF before rendering Create.
    // A missing Clerk membership with an existing BFF workspace repairs into
    // the app; only a confirmed-empty BFF may show first-run setup.
    if (!isLoaded || !isSignedIn || !membershipsKnown || membershipCount > 0 || orgId) return;
    if (bffCheckRef.current) return;
    bffCheckRef.current = true;
    let cancelled = false;
    (async () => {
      // Runs synchronously up to the first await: same timing as before,
      // but not a direct setState-in-effect (avoids a cascading render).
      setBffGate('checking');
      // Clerk client state can lag provisioning (0 memberships right after
      // create). Revalidate before trusting the empty list: if memberships
      // appear, activate instead of recovering.
      const fresh = await safeRevalidate();
      if (cancelled) return;
      if (fresh && fresh.length > 0) {
        try {
          await setActive?.({ organization: pickPersonalFirst(fresh).organization.id });
          if (cancelled) return;
          await queryClient.clear();
          router.replace('/chat');
          return;
        } catch {
          if (cancelled) return;
          // Activation failed: fall through to the BFF check below.
        }
      }
      let mine: WorkspaceItem[];
      try {
        mine = await fetchWorkspaces(api);
      } catch {
        // Unreachable or rejecting BFF (a true first run 401s here): Create stays correct.
        if (!cancelled) setBffGate('empty');
        return;
      }
      if (cancelled) return;
      if (mine.length === 0) {
        setBffGate('empty');
        return;
      }
      // Clerk may have delivered memberships while the BFF check was in
      // flight: re-check before attempting a repair.
      const during = membershipsRef.current;
      if (during && during.length > 0) {
        setBffGate('idle');
        return;
      }
      setBffGate('recovering');
      try {
        const recovered = await recoverWorkspace(api);
        if (cancelled) return;
        await setActive?.({ organization: recovered.clerk_organization_id });
        await queryClient.clear();
        router.replace('/chat');
      } catch (err) {
        if (cancelled) return;
        // No personal workspace after all (e.g. team-only BFF rows): Create is legitimate.
        if (err instanceof ApiError && err.status === 404) {
          setBffGate('empty');
          return;
        }
        // The repair may have succeeded server-side while the response
        // failed (e.g. a 502 from a slow Clerk call): revalidate once more
        // and, if a BFF-known org now appears in Clerk, enter the app
        // instead of failing.
        const post = await safeRevalidate();
        if (cancelled) return;
        const known = new Set(mine.map((w) => w.clerk_id));
        const match = (post ?? []).find((m) => known.has(m.organization.id));
        if (match) {
          try {
            await setActive?.({ organization: match.organization.id });
            await queryClient.clear();
            router.replace('/chat');
          } catch {
            if (!cancelled) setBffGate('failed');
          }
          return;
        }
        setBffGate('failed');
      }
    })();
    // Reset on cleanup: StrictMode remounts (and dep-change reruns) must be
    // able to proceed; the BFF check is read-then-idempotent-repair, safe to retry.
    return () => {
      cancelled = true;
      bffCheckRef.current = false;
    };
  }, [isLoaded, isSignedIn, membershipsKnown, membershipCount, orgId, bffRetry, api, setActive, queryClient, router, safeRevalidate]);

  if (!isLoaded) {
    if (timedOut) return <RecoveryShell />;
    return <LoadingShell />;
  }

  if (!isSignedIn) return null;

  // Redirect is in flight. Do not keep waiting on the membership list.
  if (orgId) return <LoadingShell />;

  // No workspaces at all → first-run setup, but only after the membership
  // fetch has finished and the BFF confirms there is nothing to recover.
  if (!membershipsKnown) {
    if (timedOut) return <RecoveryShell />;
    return <LoadingShell />;
  }
  if (membershipCount === 0) {
    if (bffGate === 'failed') {
      return <RecoverFailedShell onRetry={retryBffGate} />;
    }
    if (bffGate === 'empty' && sessionGate === 'ready') {
      return <FirstRunSetup />;
    }
    return <LoadingShell />;
  }

  return <LoadingShell />;
}
